import { useAppSelector, useAppDispatch } from "../app/hooks";
import {
  selectBooking,
  removeBookingVoucherCode,
} from "../features/booking/bookingSlice";
import { selectHotel } from "../features/hotel/hotelSlice";
import { type IBookingState } from "../features/booking/bookingInterface";
import { useEffect, useState } from "react";
import VoucherField from "../components/Form/VoucherField";
import { motion } from "framer-motion";
import ReservationCard from "../components/Summary/ReservationCard";
import { useTranslation } from "react-i18next";
import Trash from "../components/Svg/Trash";
import SummaryCard from "../components/Summary/SummaryCard";
import FormatPrice from "../components/Helpers/FormatPrice";
import Button from "../components/Button";
import { GTMTrackingBeginCheckout } from "../helpers/GTMTracking";
import { useNavigate } from "react-router-dom";
import ArrowSmall from "../components/Svg/ArrowSmall";
import PlusSmall from "../components/Svg/PlusSmall";
import { vatRates } from "../config/vat";

function Summary() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const [showVoucher, setShowVoucher] = useState<boolean>(false);
  const [voucherCode, setVoucherCode] = useState<IBookingState["VoucherCode"]>(
    booking.VoucherCode,
  );
  const [reservations, setReservations] = useState<any[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleValueCallback = (obj: { [key: string]: string | boolean }) => {
    if (obj.VoucherCode !== undefined) {
      setVoucherCode(obj.VoucherCode as string);
    }
  };

  const removeVoucherCode = () => {
    setVoucherCode(null);
    dispatch(removeBookingVoucherCode());
  };

  useEffect(() => {
    let reservations: any = [];
    booking.Reservations.forEach((reservation, i) => {
      const found = reservations.some((r: any) => {
        return (
          reservation.RoomCategoryId === r.RoomCategoryId &&
          reservation.RateId === r.RateId &&
          reservation.AdultCount === r.AdultCount &&
          reservation.ChildCount === r.ChildCount &&
          JSON.stringify([...(reservation.ProductIds || [])].sort()) ===
            JSON.stringify([...(r.ProductIds || [])].sort()) &&
          reservation.StartUtc === r.StartUtc &&
          reservation.EndUtc === r.EndUtc
        );
      });
      if (!found) {
        let array: any = { ...reservation };
        array.number = i;
        array.count = 1;
        reservations.push(array);
      } else {
        const array = reservations.find((r: any) => {
          return (
            reservation.RoomCategoryId === r.RoomCategoryId &&
            reservation.RateId === r.RateId &&
            reservation.AdultCount === r.AdultCount &&
            reservation.ChildCount === r.ChildCount &&
            JSON.stringify([...(reservation.ProductIds || [])].sort()) ===
              JSON.stringify([...(r.ProductIds || [])].sort()) &&
            reservation.StartUtc === r.StartUtc &&
            reservation.EndUtc === r.EndUtc
          );
        });
        array.count++;
      }
    });
    setReservations(reservations);
  }, [booking.Reservations]);

  useEffect(() => {
    setShowVoucher(booking.VoucherCode !== null);
  }, [booking.VoucherCode]);

  return (
    <motion.div
      initial={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
      animate={{ opacity: 1, y: "0px", transition: { duration: 0.4 } }}
      exit={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
      className="absolute inset-0 overflow-y-auto w-full p-4"
    >
      <SummaryCard booking={booking} />

      {reservations &&
        reservations.map(function (reservation, i) {
          return (
            <div className="mt-4 bg-custom-100 rounded-xl text-black text-fluid-base flex flex-col w-full p-4 gap">
              <ReservationCard key={i} reservation={reservation} />
            </div>
          );
        })}

      <div
        className="mt-4 rounded-full flex justify-between gap-4 items-center whitespace-nowrap text-white bg-custom-500 px-4 py-3 justify-center text-fluid-base cursor-pointer shadow"
        onClick={() => navigate("/accommodation")}
      >
        <span className="leading-none text-center">
          {t(`scenes.accommodation.addAnotherHub`)}
        </span>
        <div className="p-1 bg-white rounded-full flex items-center justify-center">
          <PlusSmall className="size-fluid-3 text-custom-500" />
        </div>
      </div>

      <div className="mt-4 bg-custom-100 rounded-xl text-black text-fluid-base flex flex-col w-full p-4 gap-4">
        {!showVoucher ? (
          <>
            <div
              className="w-full cursor-pointer underline"
              onClick={() => setShowVoucher(!showVoucher)}
            >
              {t("scenes.summary.addVoucher")}
            </div>
          </>
        ) : (
          <>
            {booking.VoucherCode === null ? (
              <div className="w-full">
                <VoucherField
                  type="text"
                  label={t("scenes.summary.voucher")}
                  name="VoucherCode"
                  value={voucherCode}
                  error={true}
                  validate={{}}
                  errorMessages={{}}
                  valueCallback={handleValueCallback}
                  validationCallBack={() => {}}
                />
              </div>
            ) : (
              <div className="w-full">
                <div>{t("scenes.summary.voucher")}</div>
                <div
                  className={`flex items-center gap-2 bg-white/25 rounded-md p-2 border border-green-600 mt-1`}
                >
                  <div className="flex-grow w-full">{booking.VoucherCode}</div>
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      removeVoucherCode();
                    }}
                  >
                    <Trash className="h-5 w-5" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-4 bg-custom-100 rounded-xl text-black text-fluid-base flex flex-col w-full p-4 gap-4 divide-y divide-dashed divide-custom-200">
        <div className="flex justify-between gap-2 text-h5 pb-4">
          <span>{t("scenes.summary.total")}</span>
          <FormatPrice price={booking.ReservationPrice.GrossValue} />
        </div>

        {Object.values(booking.ReservationPrice.Products).map((item, i) => {
          const label = item.IsCityTax
            ? t("scenes.summary.incCityTax")
            : (item[hotel.Culture.LanguageCode] ??
              item[hotel.DefaultLanguageCode]);
          return (
            <div
              className="flex justify-between gap-2 text-gray-400 text-fluid-xs"
              key={i}
            >
              <span>{label}</span>
              <FormatPrice price={Number(item.GrossValue)} />
            </div>
          );
        })}

        <div className="flex justify-between gap-2 text-gray-400 text-fluid-xs">
          <span>
            {t("scenes.summary.incVatRooms")}{" "}
            {Math.round((vatRates[hotel.HotelCode]?.rooms ?? 0) * 100)}%
          </span>
          <FormatPrice price={booking.ReservationPrice.RoomTaxValue} />
        </div>

        {booking.ReservationPrice.ProductTaxValue > 0 && (
          <div className="flex justify-between gap-2 text-gray-400 text-fluid-xs">
            <span>
              {t("scenes.summary.incVatProducts")}{" "}
              {Math.round((vatRates[hotel.HotelCode]?.products ?? 0) * 100)}%
            </span>
            <FormatPrice price={booking.ReservationPrice.ProductTaxValue} />
          </div>
        )}
      </div>

      <div className="mt-6">
        <Button
          text={t(`cart.title.summary`)}
          onClick={() => {
            // GTM DATALAYER
            GTMTrackingBeginCheckout();
            navigate("/details");
          }}
          suffix={<ArrowSmall className="size-fluid-4" />}
          disabled={
            booking.Reservations.length === 0 ||
            booking.ReservationPrice.State === "loading"
          }
        />
      </div>
    </motion.div>
  );
}

export default Summary;
